
import React from 'react';
import { ServiceTier } from '../types';

interface ServicesProps {
  onBook: () => void;
}

const TIERS: ServiceTier[] = [
  {
    id: 'elite',
    title: 'Elite Elder Care',
    description: 'A dedicated healthcare companion for senior residents, focused on chronic disease management and mental wellbeing.',
    features: [
      'Personalized Care Planning',
      'Continuous Vital Tracking',
      'Hospital Escort Services',
      'Daily Progress Reports'
    ],
    priceRange: 'Monthly Subscription',
    icon: '👤'
  },
  {
    id: 'nursing',
    title: 'Post-Op Recovery',
    description: 'Specialized nursing for recovery following major surgeries, orthopedics, or cardiac procedures.',
    features: [
      'Wound Management',
      'Medication Infusion',
      'Pain Control Support',
      'Physiotherapy Sync'
    ],
    priceRange: 'Pay per Visit',
    icon: '🏥'
  },
  {
    id: 'local-hand',
    title: 'Quick Assist',
    description: 'On-demand short-term help for medical appointments, injections, or emergency vitals check.',
    features: [
      '30-Min Arrival in ITPL',
      'Professional Dressings',
      'Emergency First Aid',
      'Diagnostic Collection'
    ],
    priceRange: 'Pay per Visit',
    icon: '⚡'
  }
];

const Services: React.FC<ServicesProps> = ({ onBook }) => {
  return (
    <section className="py-24 bg-white" id="services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">World-Class Home Healthcare</h2>
          <div className="flex justify-center mb-6">
            <div className="w-12 h-1.5 bg-[#2E4A96] rounded-l-full"></div>
            <div className="w-12 h-1.5 bg-[#1CB496] rounded-r-full"></div>
          </div>
          <p className="text-slate-500 max-w-2xl mx-auto text-lg font-medium leading-relaxed">
            We've redefined home care by combining medical expertise with local proximity, ensuring you're never more than a few minutes away from help.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {TIERS.map((tier) => (
            <div 
              key={tier.id} 
              className="bg-slate-50 rounded-[2rem] p-10 border border-slate-100 hover:border-[#1CB496]/30 hover:bg-white transition-all hover:shadow-2xl hover:shadow-[#2E4A96]/5 group"
            >
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-3xl mb-8 shadow-sm group-hover:scale-110 transition-transform group-hover:bg-[#1CB496] group-hover:text-white">
                {tier.icon}
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">{tier.title}</h3>
              <p className="text-slate-500 mb-8 text-sm leading-relaxed font-medium">{tier.description}</p>
              
              <ul className="space-y-4 mb-10">
                {tier.features.map((feature, i) => (
                  <li key={i} className="flex items-center text-sm text-slate-700 font-semibold">
                    <svg className="w-5 h-5 text-[#1CB496] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                    {feature}
                  </li>
                ))}
              </ul>
              
              <div className="pt-8 border-t border-slate-200 flex items-center justify-between">
                <span className="text-[#2E4A96] font-extrabold">{tier.priceRange}</span>
                <button 
                  onClick={onBook}
                  className="text-[#1CB496] font-bold text-sm hover:translate-x-1 transition-transform"
                >
                  Explore →
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
